import { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiCalendarLine,
  RiTimeLine,
  RiTeamLine,
  RiEditLine,
  RiUserAddLine,
  RiCheckLine,
  RiMapPinLine,
  RiInformationLine,
} from "@remixicon/react";
import { TrustJetLogoV2, TrustJetLogoWhite, TrustJetPassengerBackground, TrustJetPlane, EmptyState2Gray } from "@assets/images";
import { apiClient } from "@/lib/api";
import { Skeleton } from "@/components/ui/Skeleton";
import { selectAcceptedBidOption, selectPrimaryBidOption } from "@/features/quotes/utils";
import AddPassengerDrawer from "../components/AddPassengerDrawer";
import { formatUTCToDateTime } from "@/utils";
import { getAirportTimezone } from "@/utils/airportTimezone";
import { buildPassengerDetailsCompleteEmailHtml } from "@/utils/emailTemplates";

const ClientTripSheet = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Fetch quote data using token. Ask for `passengers` so that if the
  // client already submitted the form once (and then re-opened the email
  // link) we can pre-populate the form instead of showing an empty state
  // that would make them think their submission was lost.
  const { data: quote, isLoading, error } = useQuery({
    queryKey: ["clientTripSheet", quoteId, token],
    queryFn: () => apiClient.getQuoteByIdWithToken(quoteId, token, 'passengers'),
    //queryFn: () => apiClient.getQuoteById(quoteId),
    enabled: !!quoteId && !!token,
    //enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
  });

  // Broker profile + organization — needed to send the "Passenger Details
  // Complete" notification back to the broker after the client submits.
  // Both are fetched with the same public checkout token this page is
  // gated by, matching the pattern used in QuotePreviewPage.
  const { data: brokerProfile } = useQuery({
    queryKey: ["broker-profile", quote?.created_by, token],
    queryFn: () => apiClient.getUserByIdWithToken(quote.created_by, token),
    enabled: !!quote?.created_by && !!token,
  });

  const { data: organization } = useQuery({
    queryKey: ["broker-organization", quote?.broker_org_id, token],
    queryFn: () => apiClient.getOrganizationByIdWithToken(quote.broker_org_id, token),
    enabled: !!quote?.broker_org_id && !!token,
  });

  // Derive accepted bid and flights from quote
  const { acceptedBid, flights, passengerCount } = useMemo(() => {
    if (!quote) return { acceptedBid: null, flights: [], passengerCount: 0 };

    let accepted = null;
    for (const bidItem of (quote.bid_items || [])) {
      const bid = bidItem.bid;
      if (!bid?.bid_options) continue;
      const option = selectAcceptedBidOption(bid.bid_options);
      if (option) {
        accepted = bid;
        break;
      }
    }
    // Fallback: backend currently leaves bid_options at DRAFT after the
    // quote is accepted. When the parent quote is past acceptance, treat
    // the shortlisted option (or the first one) as the accepted one.
    if (!accepted) {
      const POST_ACCEPT_STATUSES = [
        "ACCEPTED", "PASSENGERS_PENDING", "WAITING_FLIGHT",
        "IN_TRANSIT", "COMPLETED", "DIVERTED", "SIGNED",
      ];
      const quoteStatus = (quote.status || "").toUpperCase();
      if (POST_ACCEPT_STATUSES.includes(quoteStatus)) {
        for (const bidItem of (quote.bid_items || [])) {
          const bid = bidItem.bid;
          // selectPrimaryBidOption skips REJECTED / shortlisted:false
          // rows in all fallback branches, so a bid whose options are
          // all soft-deleted doesn't get incorrectly treated as the
          // trip's accepted bid.
          const option = selectPrimaryBidOption(bid?.bid_options);
          if (option) {
            accepted = bid;
            break;
          }
        }
      }
    }
    if (!accepted) return { acceptedBid: null, flights: [], passengerCount: 0 };

    const buildFlight = (dep, arr, date) => ({
      departure_date: date,
      departure_airport: dep || "",
      arrival_airport: arr || "",
    });

    let flightList;
    if (accepted.leg_itinerary?.length > 1) {
      flightList = accepted.leg_itinerary;
    } else if (accepted.return_date) {
      const outbound =
        accepted.leg_itinerary?.[0] ||
        buildFlight(accepted.departure_airport, accepted.arrival_airport, accepted.departure_date);
      const returnLeg = buildFlight(
        outbound.arrival_airport || accepted.arrival_airport,
        outbound.departure_airport || accepted.departure_airport,
        accepted.return_date
      );
      flightList = [outbound, returnLeg];
    } else if (accepted.leg_itinerary?.length === 1) {
      flightList = accepted.leg_itinerary;
    } else {
      flightList = [
        buildFlight(accepted.departure_airport, accepted.arrival_airport, accepted.departure_date),
      ];
    }

    return {
      acceptedBid: accepted,
      flights: flightList,
      passengerCount: accepted.passenger_count || 0,
    };
  }, [quote]);

  // Route summary
  const routeSummary = useMemo(() => {
    if (!flights.length) return "";
    const dep = flights[0]?.departure_airport || "";
    const arr = flights[0]?.arrival_airport || "";
    if (flights.length > 1) return `${dep} → ${arr}`;
    return `${dep} → ${arr}`;
  }, [flights]);

  // Departure date formatted
  const departureDate = useMemo(() => {
    const date = flights[0]?.departure_date;
    if (!date) return "";
    const airport = flights[0]?.departure_airport || "";
    return formatUTCToDateTime(date, getAirportTimezone(airport), "dddd, MMMM D, YYYY");
  }, [flights]);


  // Passenger state
  const [passengers, setPassengers] = useState([]);
  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassengerIdx, setEditingPassengerIdx] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  // Track whether we've already seeded the form from the server so we
  // don't stomp on the user's in-progress edits (e.g. they added a row
  // locally, then the useQuery refetched from cache).
  const [seededFromServer, setSeededFromServer] = useState(false);

  // Server-side passengers are included via the `passengers` include on
  // getQuoteByIdWithToken. On first quote load, pre-populate the form
  // with what the client already submitted so revisits (fresh browser,
  // re-opened email link) show the saved rows instead of a blank form.
  // Format BE's YYYY-MM-DD birth_date back into MM/DD/YYYY to match the
  // form's stored shape.
  useEffect(() => {
    if (seededFromServer) return;
    if (!quote) return;
    const serverPassengers = Array.isArray(quote.passengers) ? quote.passengers : [];
    if (!serverPassengers.length) {
      // Nothing to seed, but still flip the flag so we don't re-attempt
      // on every quote refetch.
      setSeededFromServer(true);
      return;
    }
    const formatDobIso = (iso) => {
      if (!iso || typeof iso !== 'string') return '';
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
    };
    const seeded = serverPassengers.map((p) => ({
      fullName: p.passenger_name || '',
      dateOfBirth: formatDobIso(p.birth_date),
      nationality: p.nationality || '',
      quotePassengerId: p.quote_passenger_id || p.id || null,
      clientPassengerId: p.client_passenger_id || null,
    }));
    setPassengers(seeded);
    setSeededFromServer(true);
  }, [quote, seededFromServer]);

  const handleAddPassengerFromDrawer = (passengerData) => {
    if (editingPassengerIdx !== null) {
      setPassengers((prev) => {
        const next = [...prev];
        next[editingPassengerIdx] = passengerData;
        return next;
      });
      setEditingPassengerIdx(null);
    } else {
      setPassengers((prev) => {
        const isDuplicate = prev.some(
          (p) => p.fullName === passengerData.fullName && p.dateOfBirth === passengerData.dateOfBirth
        );
        if (isDuplicate) return prev;
        return [...prev, passengerData];
      });
    }
    setPassengerDrawerOpen(false);
  };

  const openEditPassenger = (pIdx) => {
    setEditingPassengerIdx(pIdx);
    setPassengerDrawerOpen(true);
  };

  const removePassenger = async (pIdx) => {
    const passenger = passengers[pIdx];
    const qpId = passenger?.quotePassengerId;
    if (qpId) {
      try {
        await apiClient.deleteQuotePassenger(qpId, token || null);
      } catch (err) {
        // Surface the failure and DON'T optimistically remove — otherwise
        // the passenger disappears from the UI while still existing
        // server-side, and a follow-up submit could try to re-link the
        // same passenger and either duplicate or error.
        console.error("Failed to delete quote passenger:", err);
        toast.error("Failed to remove passenger. Please try again.");
        return;
      }
    }
    setPassengers((prev) => prev.filter((_, i) => i !== pIdx));
  };

  // Submit handler
  const renameWithClientId = (clientId, file) => {
    const client = clientId || "unknown";
    return new File([file], `${client}_${file.name}`, { type: file.type });
  };

  const handleSubmit = async () => {
    if (passengers.length === 0 || isSubmitting) return;
    // Client-side validation — every passenger needs a full name at minimum.
    // BE would reject empty names anyway; catching it here saves a round
    // trip and gives a clear message pointing the user at what's wrong.
    const missingName = passengers.findIndex((p) => !String(p.fullName || '').trim());
    if (missingName !== -1) {
      toast.error(`Passenger ${missingName + 1} needs a full name.`);
      return;
    }
    setIsSubmitting(true);

    try {
      const clientId = quote?.client?.client_id || quote?.client?.id;

      // Split into existing (already in DB) and new passengers
      const existingPassengers = passengers.filter((p) => p.clientPassengerId);
      const newPassengers = passengers.filter((p) => !p.clientPassengerId);

      // Update existing passengers with any form changes
      if (existingPassengers.length) {
        await Promise.all(
          existingPassengers.map(async (p) => {
            let passport_url = p.existingPassportUrl || null;
            let visa_url = p.existingVisaUrl || null;

            // if (p.passportFiles?.length) {
            //   const uploaded = await apiClient.uploadFile(
            //     renameWithClientId(clientId, p.passportFiles[0]),
            //     "passport",
            //     requestToken?.token || null
            //   );
            //   passport_url = uploaded?.file_path || null;
            // }

            // if (p.visaFiles?.length) {

            //   const uploaded = await apiClient.uploadFile(
            //     renameWithClientId(clientId, p.visaFiles[0]),
            //     "visa",
            //     requestToken?.token || null
            //   );
            //   visa_url = uploaded?.file_path || null;
            // }

            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return apiClient.updateClientPassenger(p.clientPassengerId, {
              full_name: p.fullName || "",
              date_of_birth: dob,
              nationality: p.nationality || null,
              passport_num: null,
              passport_url,
              visa_url,
              weight: p.weight || null,
              additional_info: {
                dietary_catering: p.dietaryCatering || null,
                cabin_environment: p.cabinEnvironment || null,
                medical_mobility: p.medicalMobility || null,
                pet_accommodations: p.petAccommodations || null,
              },
            }, token || null);
          })
        );
      }

      // Only create new passengers via batch API
      let createdPassengers = [];
      if (newPassengers.length) {
        const newPayload = await Promise.all(
          newPassengers.map(async (p) => {
            let passport_url = p.existingPassportUrl || null;
            let visa_url = p.existingVisaUrl || null;

            // if (p.passportFiles?.length) {
            //   const uploaded = await apiClient.uploadFile(
            //     renameWithClientId(clientId, p.passportFiles[0]),
            //     "passport",
            //     requestToken?.token || null
            //   );
            //   passport_url = uploaded?.file_path || null;
            // }

            // if (p.visaFiles?.length) {
            //   const uploaded = await apiClient.uploadFile(
            //     renameWithClientId(clientId, p.visaFiles[0]),
            //     "visa",
            //     requestToken?.token || null
            //   );
            //   visa_url = uploaded?.file_path || null;
            // }

            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return {
              full_name: p.fullName || "",
              date_of_birth: dob,
              nationality: p.nationality || null,
              passport_url,
              visa_url,
              weight: p.weight || null,
              additional_info: {
                dietary_catering: p.dietaryCatering || null,
                cabin_environment: p.cabinEnvironment || null,
                medical_mobility: p.medicalMobility || null,
                pet_accommodations: p.petAccommodations || null,
              },
            };
          })
        );
        const result = await apiClient.createClientPassengersBatch(clientId, newPayload, token || null);
        createdPassengers = Array.isArray(result) ? result : (result?.data || []);

        // Merge the freshly-created client_passenger_ids back into local
        // state so that if the quote-link step (below) fails and the user
        // retries, we don't re-create the same client passengers again.
        // Assumes the BE response preserves batch order — same assumption
        // the quote_passengers payload below already makes (line indexed
        // by `i`), so this doesn't introduce a new fragility.
        if (createdPassengers.length === newPassengers.length) {
          setPassengers((prev) => {
            const idByKey = new Map();
            newPassengers.forEach((p, i) => {
              const key = `${p.fullName || ''}|${p.dateOfBirth || ''}`;
              const id = createdPassengers[i]?.client_passenger_id || createdPassengers[i]?.id;
              if (id) idByKey.set(key, id);
            });
            return prev.map((p) => {
              if (p.clientPassengerId) return p;
              const key = `${p.fullName || ''}|${p.dateOfBirth || ''}`;
              const id = idByKey.get(key);
              return id ? { ...p, clientPassengerId: id } : p;
            });
          });
        }
      }

      // Build quote passengers payload from both existing and newly created
      const qId = quote?.quote_id || quote?.id || quoteId;
      if (qId) {
        const quotePassengersPayload = [
          ...existingPassengers.filter((p) => !p.quotePassengerId).map((p) => {
            let dob = null;
            if (p.dateOfBirth) {
              const m = p.dateOfBirth.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
              dob = m ? `${m[3]}-${m[1]}-${m[2]}` : p.dateOfBirth;
            }
            return {
              passenger_name: p.fullName || '',
              email: null,
              phone: null,
              passport_num: null,
              nationality: p.nationality || null,
              birth_date: dob,
              client_passenger_id: p.clientPassengerId,
            };
          }),
          ...createdPassengers.map((cp, i) => ({
            passenger_name: cp.full_name || newPassengers[i]?.fullName || '',
            email: null,
            phone: null,
            passport_num: null,
            nationality: cp.nationality || null,
            birth_date: cp.date_of_birth || null,
            client_passenger_id: cp.client_passenger_id || null,
          })),
        ];
        let createdQuotePassengers = [];
        if (quotePassengersPayload.length) {
          const qpResult = await apiClient.createQuotePassengersBatch(qId, { passengers: quotePassengersPayload }, token || null);
          createdQuotePassengers = Array.isArray(qpResult) ? qpResult : (qpResult?.data || []);

          // Same merge story as above — stash the returned quote_passenger_id
          // on local state so a retry after this point doesn't re-link the
          // same client_passenger, which could duplicate rows or 409 on BE.
          // Matches by client_passenger_id (canonical link) rather than by
          // position, since the payload interleaves existing-existing +
          // newly-created rows.
          if (createdQuotePassengers.length) {
            const qpIdByClientId = new Map();
            createdQuotePassengers.forEach((qp) => {
              const cid = qp?.client_passenger_id;
              const qid = qp?.quote_passenger_id || qp?.id;
              if (cid && qid) qpIdByClientId.set(cid, qid);
            });
            if (qpIdByClientId.size) {
              setPassengers((prev) => prev.map((p) => {
                if (p.quotePassengerId || !p.clientPassengerId) return p;
                const qid = qpIdByClientId.get(p.clientPassengerId);
                return qid ? { ...p, quotePassengerId: qid } : p;
              }));
            }
          }
        }

        // Mark the request as completed so the broker sees the success state
        await apiClient.updateQuote(qId, {
          files: {
            ...(quote?.files || {}),
            request_client_passengers: 2,
          },
        }, token);
      }

      // Notify the broker that the client finished the passenger form.
      // Sent via the public /messages endpoint using this page's checkout
      // token — same auth path the rest of the client-side actions on
      // this page use. Fire-and-forget: don't block the "submitted"
      // toast on email delivery, but log failures so we can debug.
      try {
        const brokerEmail = brokerProfile?.email;
        if (brokerEmail) {
          const clientDisplayName =
            [quote?.client?.first_name, quote?.client?.last_name].filter(Boolean).join(' ')
            || quote?.client?.email
            || '';
          const emailHtml = await buildPassengerDetailsCompleteEmailHtml({
            quote,
            reviewUrl: `${window.location.origin}/trip-sheet?quoteid=${quote?.quote_id}`,
            flights,
            clientName: clientDisplayName,
            organization,
          });
          apiClient.sendMessage({
            From: "tools@trustjet.ai",
            To: brokerEmail,
            Subject: `TrustJet – Passenger Details Complete (${tripId || quote?.quote_number || ''})`,
            HtmlBody: emailHtml
          }, token || null)
            .then(() => console.log('Passenger details complete email sent to', brokerEmail))
            .catch((err) => console.error('Failed to send passenger details complete email:', err));
        }
      } catch (emailErr) {
        console.error('Failed to build passenger details complete email:', emailErr);
      }

      toast.success("Passenger details submitted successfully!");
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit passengers:", err);
      toast.error("Failed to submit passenger details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Quote display ID
  const tripId = quote?.quote_number || quoteId?.slice(-6)?.toUpperCase() || "";

  // Landing screen
  if (showLanding) {
    return (
      <div
        className="h-screen w-full flex flex-col bg-cover bg-no-repeat items-center justify-center px-4"
        style={{
            backgroundImage: `url('${TrustJetPlane}')`,
            backgroundColor: '#1e40af',
            backgroundPosition: 'center 40%',
          }}
      >
        <img src={TrustJetLogoV2} alt="TrustJet" className="h-8 mb-10" />
        <div className="w-full max-w-lg space-y-4">
          {isLoading ? (
            <>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <Skeleton className="h-12 w-full rounded-full" />
            </>
          ) : !token || error || !quote ? (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 text-center">
              <p className="text-sm text-gray-600">
                {!token ? "Invalid or missing access token." : "Unable to load trip details. Please check your link and try again."}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-green-100 backdrop-blur-sm rounded-2xl px-5 py-4 flex items-start gap-3">
                <RiCheckLine className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <p className="text-sm text-gray-700">
                  Your trip sheet for booking number <span className="font-bold text-gray-900">{tripId}</span> is almost complete! Please proceed and provide passenger details.
                </p>
              </div>
              <button
                onClick={() => setShowLanding(false)}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary-500 text-white text-sm  rounded-full hover:bg-primary-800 transition-colors"
              >
                Add passenger details
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Submitted screen
  if (submitted) {
    return (
      <div
        className="h-screen w-full flex flex-col bg-cover bg-no-repeat items-center justify-center px-4"
        style={{
          backgroundImage: `url('${TrustJetPlane}')`,
          backgroundColor: '#1e40af',
          backgroundPosition: 'center 40%',
        }}
      >
        <img src={TrustJetLogoV2} alt="TrustJet" className="h-8 mb-10" />
        <div className="w-full max-w-lg space-y-4">
          <div className="bg-black/30 backdrop-blur-sm rounded-2xl px-6 py-6 space-y-4">
            <h2 className="text-3xl font-bold text-white leading-tight">
              Passenger details has been submitted!
            </h2>
            <p className="text-sm text-white/80">
              Your completed trip sheet will be sent to your email shortly.
            </p>
            <div className="bg-white rounded-xl px-4 py-3 space-y-2">
              <p className="text-sm text-gray-900">
                Booking <span className="font-bold">{tripId}</span>
              </p>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <RiFlightTakeoffLine className="w-3.5 h-3.5" />
                <span>{routeSummary}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <RiTeamLine className="w-3.5 h-3.5" />
                <span>{passengers.length} passenger{passengers.length !== 1 ? 's' : ''} total</span>
              </div>
            </div>

            <div className="flex gap-2">
              <span className="text-xs text-white/60">Powered by</span>
              <img src={TrustJetLogoWhite} alt="TrustJet" className="h-4" />
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full flex items-center justify-center"
      style={{
        backgroundImage: `url(${TrustJetPassengerBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Centered card container */}
      <div className="w-full max-w-7xl h-full md:h-[92vh] flex md:rounded-[2rem] overflow-hidden">
        {/* Left branding panel */}
        <div className="hidden md:flex w-2/4 shrink-0 flex-col items-start justify-start p-10 pt-5 bg-transparent">
          <img src={TrustJetLogoV2} alt="TrustJet" className="h-6 mb-6" />
          <h1 className="text-5xl font-semibold text-white leading-tight">
            Passenger Details
          </h1>
        </div>

        {/* Right content panel — frosted white container */}
        <div className="flex-1 flex flex-col min-h-0 bg-white/70 backdrop-blur-sm md:rounded-l-[2rem]">
          {/* Mobile logo */}
          <div className="md:hidden flex items-center gap-2 p-4 pb-0">
            <img src={TrustJetLogoV2} alt="TrustJet" className="h-10" />
          </div>
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-5 md:p-8">
          {/* Error state */}
          {!token && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <p className="text-gray-600">Invalid or missing access token.</p>
            </div>
          )}

          {/* API error / no data state */}
          {token && !isLoading && (error || !quote) && (
            <div className="bg-white rounded-2xl p-8 text-center">
              <p className="text-gray-900 font-semibold mb-2">Unable to load trip details</p>
              <p className="text-sm text-gray-500">Please check your link and try again.</p>
            </div>
          )}

          {/* Loading state */}
          {token && isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full rounded-full" />
            </div>
          )}

          {/* Main content */}
          {token && !isLoading && quote && (
            <div className="space-y-4">
              {/* Trip title */}
              <h2 className="text-xl text-gray-900">
                Booking <span className="text-primary-900 font-bold">{tripId}</span>
              </h2>

              {/* Already-submitted banner. Shown when the BE has recorded
                  that the client already sent in their passenger details
                  (files.request_client_passengers === 2). Tells the user
                  they're editing an existing submission instead of
                  starting fresh, so the pre-populated form doesn't
                  surprise them. */}
              {quote?.files?.request_client_passengers === 2 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                  <RiInformationLine className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-blue-900">
                    Passenger details already submitted. You can review or update the entries below and submit again.
                  </p>
                </div>
              )}

              {/* Trip info box */}
              <div className="bg-gray-50/80 rounded-xl p-4 space-y-3">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Trip Details</p>
                <div className="flex items-start gap-8">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                      <RiTeamLine className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Passenger Count</p>
                      <p className="text-sm font-semibold text-gray-900">{passengerCount}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                      <RiMapPinLine className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">
                        {flights.length > 1 ? "Round Trip" : "One Way"}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{routeSummary}</p>
                    </div>
                  </div>
                </div>
                {departureDate && (
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0">
                      <RiCalendarLine className="w-3.5 h-3.5 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500">Departure Date</p>
                      <p className="text-sm font-semibold text-gray-900">{departureDate}</p>
                    </div>
                  </div>
                )}

                       {/* Leg itineraries */}
              <div className="space-y-1">
                {flights.map((flight, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 text-sm text-gray-700 py-2 border-t border-gray-200"
                  >
                    <span className="inline-flex items-center gap-1.5 bg-gray-100 pl-1 pr-2.5 py-1 text-xs font-medium text-gray-700 rounded-full bg-gray-200">
                      <span className="w-5 h-5 flex items-center justify-center shrink-0">
                        <RiFlightTakeoffLine className="w-3 h-3 text-gray-700" />
                      </span>
                      {flight.departure_airport}
                    </span>
                    <RiArrowRightLine className="w-3.5 h-3.5 text-gray-600" />
                    <span className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full bg-gray-200 pl-1 pr-2.5 py-1 text-xs font-medium text-gray-700">
                      <span className="w-5 h-5 flex items-center justify-center shrink-0">
                        <RiFlightLandLine className="w-3 h-3 text-gray-700" />
                      </span>
                      {flight.arrival_airport}
                    </span>
                    <span className="ml-auto flex items-center gap-3 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <RiCalendarLine className="w-3 h-3" />
                        {formatUTCToDateTime(flight.departure_date, getAirportTimezone(flight.departure_airport), "MMM D")}
                      </span>
                      {flight.departure_date && (
                        <span className="inline-flex items-center gap-1">
                          <RiTimeLine className="w-3 h-3" />
                          {formatUTCToDateTime(flight.departure_date, getAirportTimezone(flight.departure_airport), "HH:mm")}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              </div>

              {/* Passenger Cards Grid */}
              {passengers.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {passengers.map((pax, pIdx) => (
                    <div
                      key={pIdx}
                      className="flex items-center justify-between bg-white rounded-xl px-4 py-3.5 border border-gray-200"
                    >
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {pax.fullName || `Passenger ${pIdx + 1}`}
                      </span>
                      <button
                        onClick={() => openEditPassenger(pIdx)}
                        className="text-gray-400 hover:text-gray-600 transition-colors ml-2 shrink-0"
                      >
                        <RiEditLine className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 min-h-[30vh]">
                  <img src={EmptyState2Gray} alt="No passengers" className="w-28 h-28 mb-3 opacity-50" />
                  <p className="text-sm text-gray-500">No passengers added yet</p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Bottom action buttons */}
        {token && !isLoading && quote && (() => {
          // Gate on the quote's booked passenger_count. Client can't add
          // more passengers than the broker sold — button disables (with
          // a hint) once the list matches the cap.
          const atCapacity = passengerCount > 0 && passengers.length >= passengerCount;
          return (
          <div className="shrink-0 px-5 pb-5 md:px-8 md:pb-8 pt-5 border-t border-[#ededed]">
            {atCapacity && (
              <p className="text-xs text-red-500 text-center mb-2">
                You've reached the maximum of {passengerCount} passenger{passengerCount === 1 ? '' : 's'} for this trip.
              </p>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setEditingPassengerIdx(null);
                  setPassengerDrawerOpen(true);
                }}
                disabled={atCapacity}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#e2edf5] border-primary-50 text-primary-500 text-sm rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RiUserAddLine className="w-4 h-4" />
                Add Passenger
              </button>
              {passengers.length>0 && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-[1.5] flex items-center justify-center gap-2 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-800 transition-colors"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
                <RiCheckLine className="w-4 h-4" />
              </button>)}
            </div>
          </div>
          );
        })()}
      </div>
      </div>

      {/* AddPassengerDrawer */}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => {
          setPassengerDrawerOpen(false);
          setEditingPassengerIdx(null);
        }}
        onAdd={handleAddPassengerFromDrawer}
        onRemove={async () => {
          if (editingPassengerIdx !== null) {
            await removePassenger(editingPassengerIdx);
            setEditingPassengerIdx(null);
            setPassengerDrawerOpen(false);
          }
        }}
        editingPassenger={
          editingPassengerIdx !== null ? passengers[editingPassengerIdx] : null
        }
        clientId={quote?.client?.client_id || quote?.client?.id}
        token={token}
        asModal
        newOnly
      />
    </div>
  );
};

export default ClientTripSheet;
